import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';

import RouterWrapper from '../../../utils/RouterWrapper';
import ProfileStore from '../../../stores/ProfileStore';
import DocumentTitle from '../../general/DocumentTitle.react';
import SnackbarMessage from '../../general/snackbar/SnackbarMessage.react';
import { SnackbarVariants } from '../../general/snackbar/SnackbarConstants';

import CashWalletStore from './CashWalletStore';
import CashWalletActions from './CashWalletActions';
import CashWalletConstants, { getMaxAmount, maxWithdrawableNet } from './CashWalletConstants';
import CashWalletBanner from './CashWalletBanner.react';
import BalanceHeader from './BalanceHeader.react';
import RequestsSection from './RequestsSection.react';
import DetailDrawer from './DetailDrawer.react';
import WithdrawalDrawer from './WithdrawalDrawer.react';
import SuccessDialog from './SuccessDialog.react';

const { STATUS, BUTTON_STATE, CANCEL_ERROR, SUBMIT_ERROR, SUBMIT_INLINE_CODES, DEFAULT_CURRENCY, FIRST_PAGE } =
    CashWalletConstants;

// Codes no drawer field edit can resolve: close the drawer, toast the reason, refresh the dashboard.
const SUBMIT_CLOSE_CODES = [SUBMIT_ERROR.FROZEN, SUBMIT_ERROR.ACTIVE_REQUEST, SUBMIT_ERROR.MONTHLY_CAP_REACHED];
const SUBMIT_KEEP_TOAST_CODES = [SUBMIT_ERROR.UPLOAD_FAILED, SUBMIT_ERROR.CREATE_FAILED];

function getState() {
    return {
        dashboard: CashWalletStore.getDashboard(),
        dashboard_status: CashWalletStore.getDashboardStatus(),
        requests: CashWalletStore.getRequests(),
        requests_page: CashWalletStore.getRequestsPage(),
        requests_count: CashWalletStore.getRequestsCount(),
        requests_status: CashWalletStore.getRequestsStatus(),
        active_request: CashWalletStore.getActiveRequest(),
        detail: CashWalletStore.getDetail(),
        detail_status: CashWalletStore.getDetailStatus(),
        cancel_status: CashWalletStore.getCancelStatus(),
        cancel_error: CashWalletStore.getCancelError(),
        submit_status: CashWalletStore.getSubmitStatus(),
        submit_error: CashWalletStore.getSubmitError(),
        submitted_request: CashWalletStore.getSubmittedRequest(),
    };
}

const INITIAL_TOAST = { open: false, variant: SnackbarVariants.SUCCESS, message: '' };

const CashWalletPage = () => {
    const { t } = useTranslation('CashWallet');
    const navigate = useNavigate();

    const [state, setState] = useState(getState());
    const [errorBannerDismissed, setErrorBannerDismissed] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [withdrawalOpen, setWithdrawalOpen] = useState(false);
    const [successOpen, setSuccessOpen] = useState(false);
    const [toast, setToast] = useState(INITIAL_TOAST);
    const cancelStatusRef = useRef(STATUS.INITIAL);
    const submitStatusRef = useRef(STATUS.INITIAL);

    useEffect(() => {
        const onChange = () => setState(getState());

        CashWalletStore.addChangeListener(onChange);
        CashWalletActions.getDashboard();
        CashWalletActions.getRequests(FIRST_PAGE);

        return () => {
            CashWalletStore.removeChangeListener(onChange);
        };
    }, []);

    const {
        dashboard,
        dashboard_status,
        requests,
        requests_page,
        requests_count,
        requests_status,
        active_request,
        detail,
        detail_status,
        cancel_status,
        cancel_error,
        submit_status,
        submit_error,
        submitted_request,
    } = state;

    const unavailable = dashboard && dashboard.available === false;

    useEffect(() => {
        if (unavailable) {
            RouterWrapper.goToSearchPage(navigate);
        }
    }, [unavailable]);

    const refreshDashboardAndList = () => {
        CashWalletActions.getDashboard();
        CashWalletActions.getRequests(requests_page || FIRST_PAGE);
    };

    useEffect(() => {
        if (cancelStatusRef.current === cancel_status) {
            return;
        }
        cancelStatusRef.current = cancel_status;

        if (cancel_status === STATUS.SUCCESS) {
            setToast({ open: true, variant: SnackbarVariants.SUCCESS, message: t('Toast.CancelSuccess') });
            setDrawerOpen(false);
            refreshDashboardAndList();
        } else if (cancel_status === STATUS.ERROR) {
            let message;
            let variant = SnackbarVariants.WARNING;
            if (cancel_error === CANCEL_ERROR.ALREADY_IN_REVIEW) {
                message = t('Toast.CancelAlreadyInReview');
            } else if (cancel_error === CANCEL_ERROR.ALREADY_RESOLVED) {
                message = t('Toast.CancelAlreadyResolved');
            } else {
                message = t('Toast.GenericError');
                variant = SnackbarVariants.ERROR;
            }
            setToast({ open: true, variant, message });
            setDrawerOpen(false);
            refreshDashboardAndList();
        }
    }, [cancel_status, cancel_error]);

    useEffect(() => {
        if (submitStatusRef.current === submit_status) {
            return;
        }
        submitStatusRef.current = submit_status;

        if (submit_status === STATUS.SUCCESS) {
            setSuccessOpen(true);
            return;
        }
        if (submit_status !== STATUS.ERROR) {
            return;
        }

        const code = submit_error;
        if (SUBMIT_CLOSE_CODES.includes(code)) {
            setWithdrawalOpen(false);
            setToast({ open: true, variant: SnackbarVariants.WARNING, message: t(`SubmitError.${code}`) });
            refreshDashboardAndList();
        } else if (SUBMIT_INLINE_CODES.includes(code)) {
            // Surfaced inline inside WithdrawalDrawer; keep the drawer open.
        } else {
            const message = SUBMIT_KEEP_TOAST_CODES.includes(code)
                ? t(`SubmitError.${code}`)
                : t('Toast.SubmitFailed');
            setToast({ open: true, variant: SnackbarVariants.ERROR, message });
        }
    }, [submit_status, submit_error]);

    if (unavailable) {
        return null;
    }

    const loading = dashboard_status === STATUS.INITIAL || dashboard_status === STATUS.BUSY;
    const isFrozen = dashboard?.button_state?.code === BUTTON_STATE.FROZEN;
    const showErrorBanner = dashboard_status === STATUS.ERROR && !errorBannerDismissed;

    const currency = dashboard?.currency || DEFAULT_CURRENCY;
    const billTo = dashboard?.bill_to || null;
    // The drawer asks for and validates a NET amount, so it gets the server's NET spendable ceiling;
    // deriving it here is only a fallback for a payload predating `net_balance`.
    const grossBalance = Number.isFinite(dashboard?.balance) ? dashboard.balance : 0;
    const netBalance = Number.isFinite(dashboard?.net_balance)
        ? dashboard.net_balance
        : maxWithdrawableNet(grossBalance);
    // Same treatment as `netBalance`, and deliberately NOT the `|| 0` shortcut used for `threshold`
    // below: `|| 0` on a MAXIMUM would read a missing figure as "nothing may be withdrawn" and block
    // every request, where on a MINIMUM it harmlessly means "no floor".
    const maxAmount = Number.isFinite(dashboard?.max_amount) ? dashboard.max_amount : getMaxAmount(currency);
    const noFunds = dashboard?.button_state?.code === BUTTON_STATE.ZERO_BALANCE;

    const onRequestWithdrawal = () => {
        CashWalletActions.resetSubmit();
        setSuccessOpen(false);
        setWithdrawalOpen(true);
    };

    const onCloseWithdrawal = () => setWithdrawalOpen(false);

    const onSubmitWithdrawal = (payload) => {
        CashWalletActions.submitRequest(payload);
    };

    const onCloseSuccess = () => {
        setSuccessOpen(false);
        setWithdrawalOpen(false);
        refreshDashboardAndList();
    };

    const onDrawerToast = (message) => {
        setToast({ open: true, variant: SnackbarVariants.SUCCESS, message });
    };

    const onOpenRequest = (request) => {
        if (!request || !request.id) {
            return;
        }
        setDrawerOpen(true);
        CashWalletActions.getRequestDetail(request.id);
    };

    const onCloseDrawer = () => setDrawerOpen(false);

    const onPageChange = (page) => {
        CashWalletActions.getRequests(page);
    };

    const onConfirmCancel = (id) => {
        CashWalletActions.cancelRequest(id);
    };

    return (
        <DocumentTitle title={t('PageTitle')}>
            <div className="cash-wallet-page">
                {isFrozen && <CashWalletBanner variant={CashWalletBanner.VARIANT.FROZEN} message={t('Banner.Frozen')} />}
                {showErrorBanner && (
                    <CashWalletBanner
                        variant={CashWalletBanner.VARIANT.ERROR}
                        message={t('Banner.BalanceLoadError')}
                        onDismiss={() => setErrorBannerDismissed(true)}
                    />
                )}

                <BalanceHeader loading={loading} dashboard={dashboard} onRequestWithdrawal={onRequestWithdrawal} />

                <RequestsSection
                    requests={requests}
                    page={requests_page}
                    count={requests_count}
                    status={requests_status}
                    activeRequest={active_request}
                    currency={currency}
                    noFunds={noFunds}
                    onOpenRequest={onOpenRequest}
                    onPageChange={onPageChange}
                />

                <DetailDrawer
                    open={drawerOpen}
                    onClose={onCloseDrawer}
                    detail={detail}
                    detailStatus={detail_status}
                    billTo={billTo}
                    currency={currency}
                    onConfirmCancel={onConfirmCancel}
                />

                <WithdrawalDrawer
                    open={withdrawalOpen}
                    onClose={onCloseWithdrawal}
                    netBalance={netBalance}
                    currency={currency}
                    threshold={dashboard?.threshold || 0}
                    maxAmount={maxAmount}
                    expectedPayout={dashboard?.expected_payout}
                    billTo={billTo}
                    submitStatus={submit_status}
                    submitError={submit_error}
                    onSubmit={onSubmitWithdrawal}
                    onToast={onDrawerToast}
                />

                <SuccessDialog
                    open={successOpen}
                    shortId={submitted_request?.short_id}
                    email={ProfileStore.getProfile()?.email || billTo?.email}
                    onClose={onCloseSuccess}
                />

                <SnackbarMessage
                    open={toast.open}
                    variant={toast.variant}
                    message={toast.message}
                    onClose={() => setToast((prev) => ({ ...prev, open: false }))}
                />
            </div>
        </DocumentTitle>
    );
};

export default CashWalletPage;
